import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, PageHeader, Button, Field, Input, Select, Modal } from "@/components/ui-kit";
import FilePreview from "@/components/FilePreview";
import { Fuel, Plus, Trash2, Upload, Camera, Eye, Filter, X, PackagePlus, Split, AlertTriangle, Pencil } from "lucide-react";
import { formatNumber, parseQty, todayISO } from "@/lib/format";
import { DatePicker } from "@/components/DatePicker";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Iter v34br (Task 8/9) — Solar Tracking (Batching Plant)
 *   • Tipe MASUK: catat pembelian solar (liter + harga + vendor + plat mobil + foto).
 *   • Tipe DISTRIBUSI: bagi solar ke daftar alat/mobil/genset (dari master batching).
 *   • Filter rentang tanggal terhubung.
 *   • Total masuk, terbagi, dan saldo liter ditampilkan sebagai KPI.
 *   • TIDAK auto-charge ke Surat Jalan (per permintaan user).
 */
const blankForm = {
  type: "in",
  date: todayISO(),
  liter: 0,
  price_per_liter: 0,
  ref: "",
  vendor_id: "",
  vendor_name: "",
  plat_mobil: "",
  distribusi: [],
  note: "",
  proofs: [],
  // Iter v34ca (#4) — Susut
  susut_reason: "",
  susut_photo_b64: "",
};

// Iter v34ca (#4) — Watermark helper: draw timestamp + user name pada foto sblm dikirim.
async function watermarkPhoto(b64, wm) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const cv = document.createElement("canvas");
      cv.width = img.width; cv.height = img.height;
      const ctx = cv.getContext("2d");
      ctx.drawImage(img, 0, 0);
      // Semi-transparent black bar at bottom
      const barH = Math.max(50, Math.round(img.height * 0.08));
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillRect(0, img.height - barH, img.width, barH);
      ctx.fillStyle = "#fff";
      ctx.font = `bold ${Math.round(barH * 0.35)}px sans-serif`;
      ctx.textBaseline = "middle";
      const line1 = wm.timestamp;
      const line2 = `${wm.company || "RBB ERP"} · ${wm.user || ""}`;
      ctx.fillText(line1, 16, img.height - barH + barH * 0.32);
      ctx.font = `${Math.round(barH * 0.28)}px sans-serif`;
      ctx.fillText(line2, 16, img.height - barH + barH * 0.72);
      resolve(cv.toDataURL("image/jpeg", 0.82).split(",")[1]);
    };
    img.onerror = () => resolve(b64);
    img.src = `data:image/jpeg;base64,${b64}`;
  });
}

export default function BatchingSolar() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "engineer", "admin"].includes(user?.role);
  const [data, setData] = useState({ entries: [], total_in_liter: 0, total_distribusi_liter: 0, balance_liter: 0 });
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...blankForm });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [alat, setAlat] = useState([]);
  const [mobil, setMobil] = useState([]);
  const [genset, setGenset] = useState([]);
  const [editId, setEditId] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return api.get("/batching/solar-entries", { params })
      .then((r) => setData(r.data || { entries: [] }))
      .catch(() => setData({ entries: [] }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/vendors", { params: { active_only: true } }).then((r) => setVendors(r.data || [])).catch(() => {});
    api.get("/batching/alat").then((r) => setAlat(r.data || [])).catch(() => {});
    // Iter v34ca (#3) — /batching/mobil deprecated → pakai /batching/trucks.
    // Iter v34co (#3) — Tampilkan SEMUA truk (Ready Mix + Material Dump) di dropdown distribusi
    // solar. User request: pencatatan solar harus terhubung ke semua truk Master Batching.
    api.get("/batching/trucks").then((r) => setMobil(r.data || [])).catch(() => setMobil([]));
    api.get("/batching/genset").then((r) => setGenset(r.data || [])).catch(() => {});
  }, []);

  const allTargets = useMemo(() =>
    [...alat.map((x) => ({ ...x, kind: "alat" })),
     ...mobil.map((x) => ({ ...x, kind: "truk" })),
     ...genset.map((x) => ({ ...x, kind: "genset" }))],
    [alat, mobil, genset]);

  const openNew = (type) => {
    setForm({ ...blankForm, type });
    setEditId(null);
    setOpen(true);
  };
  const openEdit = (e) => {
    setForm({
      type: e.type, date: e.date, liter: e.liter,
      price_per_liter: e.price_per_liter || 0, ref: e.ref || "",
      vendor_id: e.vendor_id || "", vendor_name: e.vendor_name || "",
      plat_mobil: e.plat_mobil || "", distribusi: e.distribusi || [],
      note: e.note || "", proofs: e.proofs || [],
      susut_reason: e.susut_reason || "", susut_photo_b64: e.susut_photo_b64 || "",
    });
    setEditId(e.id);
    setOpen(true);
  };
  // Iter v34ca (#4) — Camera capture with watermark for susut foto
  const captureSusutFoto = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
      const video = document.createElement("video");
      video.srcObject = stream; video.playsInline = true; await video.play();
      // Wait 300ms for frame
      await new Promise((r) => setTimeout(r, 500));
      const cv = document.createElement("canvas");
      cv.width = video.videoWidth || 1280; cv.height = video.videoHeight || 720;
      cv.getContext("2d").drawImage(video, 0, 0, cv.width, cv.height);
      stream.getTracks().forEach((t) => t.stop());
      const raw = cv.toDataURL("image/jpeg", 0.85).split(",")[1];
      const wm = await watermarkPhoto(raw, {
        timestamp: new Date().toLocaleString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }),
        user: user?.name || user?.email || "",
        company: "RBB",
      });
      setForm((f) => ({ ...f, susut_photo_b64: wm }));
      toast.success("Foto susut diambil (dgn watermark waktu & tanggal)");
    } catch (e) { toast.error("Kamera tidak tersedia: " + (e.message || "")); }
  };

  const addDist = () => setForm((f) => ({ ...f, distribusi: [...(f.distribusi || []), { kind: "alat", id: "", name: "", liter: 0 }] }));
  const setDist = (i, k, v) => setForm((f) => {
    const arr = [...(f.distribusi || [])];
    arr[i] = { ...arr[i], [k]: v };
    return { ...f, distribusi: arr };
  });
  const rmDist = (i) => setForm((f) => ({ ...f, distribusi: (f.distribusi || []).filter((_, idx) => idx !== i) }));

  const uploadProof = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((prev) => ({ ...prev, proofs: [...(prev.proofs || []), { id: r.data.id, filename: r.data.filename, content_type: r.data.content_type }] }));
      }
      toast.success("Foto/dokumen terunggah");
    } catch { toast.error("Gagal unggah"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const rmProof = (idx) => setForm((prev) => ({ ...prev, proofs: (prev.proofs || []).filter((_, i) => i !== idx) }));

  const submit = async () => {
    if (form.liter <= 0) { toast.error("Liter harus > 0"); return; }
    if (form.type === "distribusi") {
      const total = (form.distribusi || []).reduce((s, d) => s + parseQty(d.liter), 0);
      if (Math.abs(total - parseQty(form.liter)) > 0.01) {
        toast.error(`Total distribusi (${total} L) harus = total liter (${form.liter} L)`);
        return;
      }
    }
    // Iter v34ca (#4) — Susut validation (non-admin wajib alasan + foto).
    if (form.type === "susut" && !isAdmin) {
      if (!(form.susut_reason || "").trim()) { toast.error("Alasan susut wajib diisi"); return; }
      if (!(form.susut_photo_b64 || "").trim()) { toast.error("Foto susut wajib diambil dari kamera"); return; }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        liter: parseQty(form.liter),
        price_per_liter: parseQty(form.price_per_liter),
        distribusi: (form.distribusi || []).map((d) => ({ ...d, liter: parseQty(d.liter) })),
      };
      if (editId) {
        await api.patch(`/batching/solar-entries/${editId}`, payload);
        toast.success("Entry solar diperbarui");
      } else {
        await api.post("/batching/solar-entries", payload);
        toast.success(form.type === "in" ? "Solar masuk tercatat" : form.type === "susut" ? "Susut solar tercatat" : "Distribusi tercatat");
      }
      setOpen(false);
      setForm({ ...blankForm });
      setEditId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus entry solar?")) return;
    try { await api.delete(`/batching/solar-entries/${id}`); toast.success("Terhapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8 space-y-4">
      <PageHeader
        overline="Batching Plant · Bahan Bakar"
        title="Pencatatan Solar"
        description="Solar masuk (pembelian) & distribusi ke alat/mobil/genset. Foto bukti + tanggal."
        icon={Fuel}
        action={<div className="flex gap-2">
          <Button variant="secondary" onClick={() => openNew("in")} data-testid="solar-new-in-btn"><PackagePlus className="h-4 w-4"/> Solar Masuk</Button>
          <Button onClick={() => openNew("distribusi")} data-testid="solar-new-dist-btn"><Split className="h-4 w-4"/> Distribusi</Button>
          <Button variant="secondary" onClick={() => openNew("susut")} data-testid="solar-new-susut-btn" className="!bg-red-50 !border-red-300 !text-red-800 hover:!bg-red-100"><AlertTriangle className="h-4 w-4"/> Susut</Button>
        </div>}
      />

      {/* KPI + Filter */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card className="p-4"><div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Total Masuk</div><div className="text-2xl font-bold font-mono-num text-stone-900 mt-1">{formatNumber(data.total_in_liter, 2)} <span className="text-sm text-stone-500">L</span></div></Card>
        <Card className="p-4"><div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">Terdistribusi</div><div className="text-2xl font-bold font-mono-num text-stone-900 mt-1">{formatNumber(data.total_distribusi_liter, 2)} <span className="text-sm text-stone-500">L</span></div></Card>
        <Card className="p-4"><div className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold">Saldo</div><div className={`text-2xl font-bold font-mono-num mt-1 ${data.balance_liter < 0 ? "text-red-700" : "text-stone-900"}`}>{formatNumber(data.balance_liter, 2)} <span className="text-sm text-stone-500">L</span></div></Card>
        <Card className="p-4"><div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Total Entry</div><div className="text-2xl font-bold text-stone-900 mt-1">{data.entries?.length || 0}</div></Card>
      </div>

      <Card className="p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-stone-600 mr-2">
            <Filter className="h-3.5 w-3.5"/> Rentang Tanggal
          </div>
          <Field label="Dari"><DatePicker value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="solar-filter-from"/></Field>
          <Field label="Sampai"><DatePicker value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="solar-filter-to"/></Field>
          {(dateFrom || dateTo) && <Button variant="ghost" onClick={() => { setDateFrom(""); setDateTo(""); }}><X className="h-3.5 w-3.5"/> Reset</Button>}
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-600 border-b border-stone-200">
              <tr>
                <th className="text-left py-3 px-3">Tanggal</th>
                <th className="text-left py-3 px-3">Tipe</th>
                <th className="text-right py-3 px-3">Liter</th>
                <th className="text-right py-3 px-3">Harga/L</th>
                <th className="text-left py-3 px-3">Vendor / Distribusi</th>
                <th className="text-left py-3 px-3">Plat / Ref</th>
                <th className="text-left py-3 px-3">Bukti</th>
                <th className="text-right py-3 px-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={8} className="py-8 text-center text-stone-400">Memuat...</td></tr>
              : (data.entries || []).length === 0 ? <tr><td colSpan={8} className="py-8 text-center text-stone-400">Belum ada entry solar</td></tr>
              : data.entries.map((e) => (
                <tr key={e.id} className="border-b border-stone-100" data-testid={`solar-row-${e.id}`}>
                  <td className="py-2.5 px-3 font-mono">{e.date}</td>
                  <td className="py-2.5 px-3">
                    {e.type === "in" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] uppercase font-semibold rounded"><PackagePlus className="h-3 w-3"/> Masuk</span>
                    ) : e.type === "susut" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-800 text-[10px] uppercase font-semibold rounded"><AlertTriangle className="h-3 w-3"/> Susut</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] uppercase font-semibold rounded"><Split className="h-3 w-3"/> Distribusi</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono-num font-semibold">{formatNumber(e.liter, 2)}</td>
                  <td className="py-2.5 px-3 text-right font-mono-num text-stone-500">{e.type === "in" ? formatNumber(e.price_per_liter || 0, 0) : "—"}</td>
                  <td className="py-2.5 px-3 text-xs">
                    {e.type === "in" ? (
                      <span>{e.vendor_name || <span className="text-stone-400 italic">tanpa vendor</span>}</span>
                    ) : (
                      <div className="space-y-0.5">
                        {(e.distribusi || []).map((d, i) => (
                          <div key={i} className="text-stone-700"><span className="text-[9px] uppercase tracking-wider text-stone-500 mr-1">[{d.kind}]</span>{d.name}: <b>{formatNumber(d.liter, 2)} L</b></div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-xs font-mono text-stone-500">{e.plat_mobil || "—"}<br/>{e.ref || ""}</td>
                  <td className="py-2.5 px-3">
                    {(e.proofs || []).length > 0 ? (
                      <div className="flex gap-1">
                        {(e.proofs || []).slice(0, 3).map((p, i) => (
                          <button key={i} onClick={() => setPreviewFile({ id: p.id, name: p.filename, ct: p.content_type })} className="text-blue-600 hover:text-blue-800" title={p.filename}>
                            <Eye className="h-3.5 w-3.5"/>
                          </button>
                        ))}
                      </div>
                    ) : <span className="text-stone-300 text-xs">—</span>}
                  </td>
                  <td className="py-2.5 px-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(e)} className="text-stone-500 hover:text-terra-700 px-1" title="Edit" data-testid={`solar-edit-${e.id}`}><Pencil className="h-3.5 w-3.5"/></button>
                    <button onClick={() => del(e.id)} className="text-stone-500 hover:text-red-700 px-1" title="Hapus"><Trash2 className="h-3.5 w-3.5"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal */}
      <Modal open={open} onClose={() => setOpen(false)} title={editId ? `Edit Entry Solar (${form.type})` : (form.type === "in" ? "Solar Masuk (Pembelian)" : form.type === "susut" ? "Catat Susut Solar" : "Distribusi Solar ke Alat")} size="lg">
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Field label="Tanggal"><DatePicker value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}/></Field>
            <Field label="Total Liter"><Input type="number" step="0.01" className="text-right font-mono-num" value={form.liter} onChange={(e) => setForm({ ...form, liter: e.target.value })} data-testid="solar-form-liter"/></Field>
            {form.type === "in" && <Field label="Harga / Liter (Rp)"><Input type="number" step="1" className="text-right font-mono-num" value={form.price_per_liter} onChange={(e) => setForm({ ...form, price_per_liter: e.target.value })}/></Field>}
            <Field label="No. Ref (Nota/PO)"><Input value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })} placeholder="mis: NOTA-BBM-001"/></Field>
          </div>
          {form.type === "in" && (
            <div className="grid grid-cols-2 gap-2">
              <Field label="Vendor Pengirim">
                <Select value={form.vendor_id} onChange={(e) => {
                  const v = vendors.find((x) => x.id === e.target.value);
                  setForm({ ...form, vendor_id: e.target.value, vendor_name: v?.name || "" });
                }} data-testid="solar-form-vendor">
                  <option value="">— Pilih —</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </Select>
              </Field>
              <Field label="Plat Mobil Pengantar"><Input value={form.plat_mobil} onChange={(e) => setForm({ ...form, plat_mobil: e.target.value })} placeholder="BG 1234 XY"/></Field>
            </div>
          )}
          {form.type === "distribusi" && (
            <div className="border border-stone-200 rounded p-3 bg-stone-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-stone-600">Distribusi ke Alat/Truk/Genset</span>
                <Button variant="secondary" size="sm" onClick={addDist} data-testid="solar-dist-add"><Plus className="h-3 w-3"/> Baris</Button>
              </div>
              {(form.distribusi || []).length === 0 && <div className="text-xs text-stone-400 italic py-2">Klik + Baris untuk tambah distribusi.</div>}
              <div className="space-y-2">
                {(form.distribusi || []).map((d, i) => {
                  const list = d.kind === "alat" ? alat : d.kind === "truk" ? mobil : genset;
                  // Iter v34co (#3) — Master Batching truk pakai field `plate`, bukan `name`.
                  // Fallback ke `name` untuk alat/genset supaya kompatibel.
                  const labelOf = (x) => x?.plate || x?.name || "(Tanpa nama)";
                  return (
                    <div key={i} className="grid grid-cols-12 gap-1.5 items-end">
                      <div className="col-span-3">
                        <Select value={d.kind} onChange={(e) => setDist(i, "kind", e.target.value)}>
                          <option value="alat">Alat Berat</option>
                          <option value="truk">Truk</option>
                          <option value="genset">Genset</option>
                        </Select>
                      </div>
                      <div className="col-span-6">
                        <Select value={d.id} onChange={(e) => {
                          const t = list.find((x) => x.id === e.target.value);
                          setDist(i, "id", e.target.value);
                          setDist(i, "name", labelOf(t));
                        }} data-testid={`solar-dist-target-${i}`}>
                          <option value="">— Pilih {d.kind} —</option>
                          {list.map((x) => <option key={x.id} value={x.id}>{labelOf(x)}</option>)}
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Input type="number" step="0.01" placeholder="Liter" className="text-right font-mono-num" value={d.liter} onChange={(e) => setDist(i, "liter", e.target.value)} data-testid={`solar-dist-liter-${i}`}/>
                      </div>
                      <div className="col-span-1 text-right">
                        <button onClick={() => rmDist(i)} className="text-stone-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5"/></button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 text-[10px] text-stone-500 text-right">
                Total: <b className="font-mono">{formatNumber((form.distribusi || []).reduce((s, d) => s + parseQty(d.liter), 0), 2)} L</b> / target <b className="font-mono">{formatNumber(parseQty(form.liter), 2)} L</b>
              </div>
            </div>
          )}
          {/* Iter v34ca (#4) — Susut section (Non-admin wajib alasan + foto watermark) */}
          {form.type === "susut" && (
            <div className="border border-red-200 rounded p-3 bg-red-50/50 space-y-3">
              <div className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="h-4 w-4"/>
                <span className="text-sm font-semibold">Laporan Susut/Kehilangan Solar</span>
              </div>
              <Field label={isAdmin ? "Alasan susut (opsional)" : "Alasan susut *"}>
                <Input
                  value={form.susut_reason}
                  onChange={(e) => setForm({ ...form, susut_reason: e.target.value })}
                  placeholder="Contoh: tangki bocor, tumpah saat pemindahan, dst."
                  data-testid="solar-susut-reason"
                />
              </Field>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-600 font-semibold mb-1.5">
                  {isAdmin ? "Foto bukti (opsional)" : "Foto bukti (wajib, dgn watermark waktu & tanggal) *"}
                </div>
                {form.susut_photo_b64 ? (
                  <div className="relative inline-block">
                    <img src={`data:image/jpeg;base64,${form.susut_photo_b64}`} alt="susut" className="max-h-48 border border-red-300 rounded"/>
                    <button
                      onClick={() => setForm({ ...form, susut_photo_b64: "" })}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-0.5 hover:bg-red-700"
                      title="Hapus foto"
                    ><X className="h-3 w-3"/></button>
                  </div>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={captureSusutFoto}
                    className="!bg-red-100 !text-red-800 hover:!bg-red-200"
                    data-testid="solar-susut-capture"
                  ><Camera className="h-4 w-4"/> Ambil Foto dari Kamera</Button>
                )}
                <div className="text-[10px] text-stone-500 mt-1">Foto akan otomatis diberi watermark: waktu, tanggal, perusahaan, dan nama Anda.</div>
              </div>
              {isAdmin && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  <b>Mode Admin:</b> Anda dapat menyimpan tanpa alasan atau foto (untuk kasus urgent).
                </div>
              )}
            </div>
          )}
          <Field label="Catatan (opsional)"><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}/></Field>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-stone-600 mb-1.5">Foto Bukti / Nota</label>
            <div className="flex flex-wrap gap-2">
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 rounded cursor-pointer hover:bg-stone-50 text-sm">
                <Upload className="h-4 w-4"/>{uploading ? "Mengunggah..." : "Pilih file"}
                <input type="file" multiple accept=".jpg,.jpeg,.png,.pdf" onChange={uploadProof} className="hidden" data-testid="solar-proof-input"/>
              </label>
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-terra-300 bg-terra-50 rounded cursor-pointer hover:bg-terra-100 text-sm text-terra-800">
                <Camera className="h-4 w-4"/>Foto Langsung
                <input type="file" accept="image/*" capture="environment" onChange={uploadProof} className="hidden" data-testid="solar-camera-input"/>
              </label>
            </div>
            {(form.proofs || []).length > 0 && (
              <div className="mt-2 space-y-1 border border-stone-200 rounded p-2 bg-stone-50/50">
                {(form.proofs || []).map((pf, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs">
                    <span className="text-emerald-800 truncate flex-1">{pf.filename}</span>
                    <button type="button" onClick={() => setPreviewFile({ id: pf.id, name: pf.filename, ct: pf.content_type })} className="text-blue-600 hover:text-blue-700"><Eye className="h-3.5 w-3.5"/></button>
                    <button type="button" onClick={() => rmProof(idx)} className="text-stone-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5"/></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-stone-100">
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submit} disabled={saving} data-testid="solar-form-submit"><Plus className="h-3.5 w-3.5"/>{saving ? "Menyimpan..." : "Simpan"}</Button>
          </div>
        </div>
      </Modal>

      <FilePreview open={!!previewFile} onClose={() => setPreviewFile(null)} fileId={previewFile?.id} filename={previewFile?.name} contentType={previewFile?.ct}/>
    </div>
  );
}
